// Website v2: single source of truth for copy and facts.
// Rules (from brand guidelines + legal hub, Oct 2026):
// - Plain, warm, from the trade. Start where it hurts. One idea at a time. No em-dashes.
// - No invented statistics. Every figure here is a fact we can stand behind (price list, terms, trust centre).
// - Do NOT name customers or prospects. Approved social proof: "leading European manufacturers".
// - Label honestly: 'live' = built and in production use; 'pilot' = built, rolling out; 'next' = planned.
// - Never publish: data model, admin internals, ERP field mappings, AI vendor, unit economics, contract terms.

export const brand = {
  name: 'BrandedMark',
  line: 'The ownership layer for physical products',
  filmLine: 'Physical things need an operating system.',
  signoff: 'Make owning things better.',
  belief: 'Owners judge your brand on setup, repairs and parts, long after the sale.',
  boilerplate:
    'BrandedMark gives every product you make a living record. Dealers register it at handover, owners scan it for manuals, parts and help, and you finally see who owns what, connected to the ERP you already run.',
  socialProof: 'Chosen by a leading European manufacturer',
};

export type Status = 'live' | 'pilot' | 'next';

export const cta = {
  primary: 'Book 30 minutes',
  primaryLong: 'Book 30 minutes. Bring one product.',
  primaryHref: '/book-a-demo',
  secondary: 'Take the tour',
  secondaryHref: '/tour',
  scan: 'Scan a live example',
  pilot: 'Pilot one product line',
  priceList: 'See the price list',
  priceListHref: '/legal/pricing/',
  trustHref: '/trust/',
};

export type NavItem = { label: string; href: string; desc: string; also?: { label: string; href: string } };
export type NavGroup = { title: string; items: NavItem[] };
export type NavFeature = { title: string; body: string; cta: { label: string; href: string }; more: { label: string; href: string; desc: string } };
export type NavEntry = { label: string; href: string; items?: NavItem[]; groups?: NavGroup[]; feature?: NavFeature; foot?: string };

// Platform: a tour card plus three short groups (less choice: nine links, not ten equal items).
const platformGroups: NavGroup[] = [
  {
    title: 'For owners',
    items: [
      { label: 'Registration & warranty', href: '/platform/registration-warranty', desc: 'Warranty that starts at handover.' },
      { label: 'Intelligent products', href: '/platform/ai', desc: 'An expert for every unit. No guessing.' },
      { label: 'Ownership transfer', href: '/platform/ownership', desc: 'The record moves with the product.' },
    ],
  },
  {
    title: 'For your team',
    items: [
      { label: 'Dealers & installers', href: '/platform/dealers', desc: 'Your channel stays in every sale.' },
      {
        label: 'Service, parts & claims',
        href: '/platform/service',
        desc: 'Claims that arrive complete, and the right spare for that unit.',
        also: { label: 'Spares & accessories', href: '/platform/parts' },
      },
      { label: 'Forms & signatures', href: '/platform/forms', desc: 'Your PDFs, signed on a phone and sealed.' },
    ],
  },
  {
    title: 'Connects to',
    items: [
      {
        label: 'ERP integrations',
        href: '/integrations',
        desc: 'Epicor, IFS, SAP. Your ERP stays master.',
        also: { label: 'Epicor Kinetic connector', href: '/integrations/epicor-kinetic' },
      },
      { label: 'Digital Product Passport', href: '/platform/digital-product-passport', desc: 'Passport data on the same code.' },
    ],
  },
];

export const nav: { primary: NavEntry[] } = {
  primary: [
    {
      label: 'Platform',
      href: '/platform',
      feature: {
        title: 'See it on one unit.',
        body: 'Click through the owner page, the installer handover and your view of the record.',
        cta: { label: 'Take the tour', href: '/tour' },
        more: { label: 'How it works', href: '/platform', desc: 'The whole platform on one page.' },
      },
      groups: platformGroups,
      // Flat list for anything that only reads items (sitemaps, simple menus)
      items: platformGroups.flatMap((g) => g.items),
    },
    {
      label: 'Industries',
      href: '/industries',
      items: [], // filled from industries below
      foot: 'Make something else that lasts? We probably fit.',
    },
    { label: 'Trust', href: '/why-trust-us' },
    { label: 'Pricing', href: '/pricing' },
    {
      label: 'Resources',
      href: '/blog/',
      items: [
        { label: 'Why we exist', href: '/why', desc: 'Things should not be disposable.' },
        { label: 'Customer story', href: '/customers', desc: 'A leading European manufacturer, packing slips first.' },
        { label: 'Blog', href: '/blog/', desc: 'Field notes on aftersales, dealers and DPP.' },
        { label: 'Trust Centre', href: '/trust/', desc: 'Security, data protection, sub-processors.' },
      ],
    },
  ],
};

// Hero options (buyer-psychology research). Variants pick one; keep alternates for A/B tests.
export const heroes = {
  machines: {
    title: ['Know who owns every', 'machine', 'you have ever sold.'], // middle element carries the swipe
    sub: 'Registration, warranty, parts and service history for every serial number. Under your brand, connected to your ERP, with your dealers kept in the loop.',
  },
  handover: {
    title: ['The warranty starts at', 'handover', 'Not when the form turns up.'],
    sub: 'Dealers register each unit on a phone at delivery. Owners scan for manuals, parts and help. You see every unit, owner and claim.',
  },
  relationship: {
    title: ['Your products leave the factory.', 'Your relationship', "with them shouldn't."],
    sub: 'A living record for every unit you make, for every owner it has, with service, parts and passport data attached.',
  },
  accounted: {
    title: ['Every unit you have built,', 'accounted for.', ''],
    sub: 'Serial-level records for registration, ownership, service and recalls. On your domain, built for GDPR, fed by the ERP you already run.',
  },
};

// The problem, told as real moments (no stats)
export const problem = {
  eyebrow: 'The problem',
  title: ['The moment a product ships,', 'the brand goes quiet.'],
  lede: 'Most manufacturers learn who owns their product when something goes wrong. By then the dealer has moved on, the card is in the bin and the claim arrives by email with no serial.',
  moments: [
    { k: 'The card', t: 'The warranty card comes back late, or never.', d: 'So cover starts on the wrong date and nobody can prove when the machine was handed over.' },
    { k: 'The manual', t: 'The owner has the wrong manual for their build year.', d: 'They phone support, or worse, they guess.' },
    { k: 'The part', t: 'Parts come from whoever ranks first online.', d: 'Revenue you earned goes to a stranger, and the fit is a gamble.' },
    { k: 'The recall', t: 'A service bulletin reaches the dealer, not the operator.', d: 'The people using the machine are the last to know.' },
    { k: 'The resale', t: 'The second owner is invisible.', d: 'Your relationship ends at the first sale, along with the service history.' },
  ],
};

// How it works: three steps. Explainers animate these.
export const howItWorks = {
  eyebrow: 'How it works',
  title: ['Tagged at the factory,', 'registered at handover.'],
  steps: [
    {
      n: '01',
      k: 'Tag',
      t: 'Every unit leaves with an identity',
      d: 'A QR or NFC tag on the plate, box or packing slip, fed from your ERP.',
    },
    {
      n: '02',
      k: 'Hand over',
      t: 'The dealer scans at delivery',
      d: 'Checks and handover signed on a phone. Warranty starts that day.',
    },
    {
      n: '03',
      k: 'Own',
      t: 'Owners scan for everything',
      d: 'The right manual, genuine parts, service and support. No app, no password.',
    },
  ],
};

export const capabilities: { key: string; title: string; line: string; detail: string; status: Status; href: string }[] = [
  { key: 'identity', title: 'Product identity', line: 'One code per unit, on your domain.', detail: 'QR or NFC through GS1 Digital Link, with labels printed from your serials.', status: 'live', href: '/platform#how' },
  { key: 'registration', title: 'Registration & warranty', line: 'Warranty that starts at handover.', detail: 'Owners scan to register. Dealers register at delivery.', status: 'live', href: '/platform/registration-warranty' },
  { key: 'dealers', title: 'Dealers & installers', line: 'Your channel stays in every sale.', detail: 'The selling dealer is credited, and handover forms are signed on a phone.', status: 'pilot', href: '/platform/dealers' },
  { key: 'service', title: 'Service, claims & returns', line: 'Claims that arrive complete.', detail: 'Each case carries the serial and its history. Returns go back to your ERP.', status: 'live', href: '/platform/service' },
  { key: 'parts', title: 'Parts & accessories', line: 'The right part for that exact unit.', detail: 'Matched to serial and build, sold by you or your dealers.', status: 'pilot', href: '/platform/parts' },
  { key: 'ownership', title: 'Ownership transfer', line: 'The record moves with the product.', detail: 'Later owners take over with consent, keeping warranty and history.', status: 'live', href: '/platform/ownership' },
  { key: 'dpp', title: 'Digital Product Passport', line: 'Passport data on the same code.', detail: 'Passport data on the same identifier, added as each EU act lands.', status: 'live', href: '/platform/digital-product-passport' },
  { key: 'support', title: 'Grounded AI support', line: 'Answers from your manuals, with sources.', detail: 'It knows that unit\'s records and hands over to a person when it should.', status: 'live', href: '/platform/service' },
  { key: 'recalls', title: 'Recalls & bulletins', line: 'Bulletins that reach the operator.', detail: 'Target the affected serials and tell the people who use them.', status: 'pilot', href: '/platform/service' },
];

// The maker's back office, from what really ships (inventory of the app, 20 Oct 2026). Outcomes, not screens.
export const backOffice = {
  title: ['Who has it? What goes wrong?', 'What does it earn?'],
  lede: 'The same records power your back office. Boards you arrange yourself, an inbox where the AI drafts and your team decides, and the tools to act on what you learn.',
  items: [
    { t: 'Boards and analytics', d: 'Registrations, questions, cases and revenue as KPIs, trends and work queues, by product line.', status: 'live' as Status },
    { t: 'One inbox, AI alongside', d: 'Owner conversations with the unit attached. The AI drafts, your team takes over in one click.', status: 'live' as Status },
    { t: 'Topics and recommendations', d: 'What owners ask, what is rising, and what your documents cannot answer yet.', status: 'live' as Status },
    { t: 'Cases, returns and recalls', d: 'Decide claims with the full history, then reach the affected serials.', status: 'live' as Status },
    { t: 'Parts, accessories and cover', d: 'Orders and revenue from owners, sold by you or your dealers.', status: 'pilot' as Status },
    { t: 'Roles, imports and exports', d: 'Your people, your permissions. Bring data in by file or API and take all of it out.', status: 'live' as Status },
  ],
};

// THE central argument (founder, 20 Oct): every system stops at the factory gate or the till.
// After that no one knows who has what, so support has no context. We give everything a digital identity.
export const gap = {
  eyebrow: 'The gap after the sale',
  title: ['Your systems stop', 'at the factory gate.'],
  lede: 'Your ERP knows what you built. Your CRM knows who you sold to. Your dealer knows who they sold to, for a while. Then the product leaves, and nobody knows who has it, what has been done to it, or what is wrong with it.',
  before: { label: 'Before the sale', systems: ['ERP', 'Production', 'CRM', 'Dealer systems', 'E-commerce'] },
  line: 'The factory gate, the till',
  after: { label: 'After the sale', unknowns: ['Who owns it now?', 'Which build is it?', 'Has it been serviced?', 'Is it still under warranty?', 'Who sold it on?'] },
  why: 'That is why getting support is so hard. Every call starts from nothing: what model, which serial, when did you buy it, who installed it. Context is everything, and it is missing.',
  answer: { title: ['We give every product', 'a digital identity.'], body: 'One identity per unit, from the line to the last owner. Everything that happens to it is attached to it, so whoever scans it, owner, installer, engineer or your support team, starts with the full story.' },
  context: ['Exact model and build', 'Owner and where it is', 'Warranty status', 'Who installed it', 'Service and parts history', 'Previous owners'],
};

// AI: physical products that can answer for themselves. Concrete and grounded, never "AI-powered" fluff.
// Sources: July/Aug decks (AI slides), product concepts (grounded AI, human takeover, resolution loop, agent-ready).
export const intelligence = {
  eyebrow: 'Intelligent products',
  title: ['AI that answers from', 'your manuals.'],
  lede: 'Every unit gets an assistant that knows its model, build and history, cites your manuals, and hands over to your team.',
  line: 'Don\'t guess. Get it right.',
  points: [
    { t: 'Knows the unit', d: 'Serial, build, warranty, owner and service history.', status: 'live' as Status },
    { t: 'Shows its sources', d: 'Every answer cites the manual page or document it came from.', status: 'live' as Status },
    { t: 'Follows your rules', d: 'Your tone, your escalation and safety rules, and only the documents you approve.', status: 'live' as Status },
    { t: 'Your team can take over', d: 'Every conversation is visible, linked to the unit, and one tap away from a person.', status: 'live' as Status },
    { t: 'Gets better with every case', d: 'Each fix is added to the record, so the next owner gets the answer first time.', status: 'live' as Status },
    { t: 'Ready for AI assistants', d: 'The AI tools your customers already use can read your product data, on your terms.', status: 'pilot' as Status },
  ],
  team: 'AI drafts forms and answers from your documents. A person approves each one.',
  why: [
    { k: 'AI is ready', d: 'Grounded AI works, but only with the exact product context.' },
    { k: 'Repair is expected', d: 'Right to repair and sustainability rules are raising the bar after the sale.' },
    { k: 'Products need memory', d: 'Digital instructions and product passports need an identity on every unit.' },
  ],
  sample: {
    q: 'It is showing E21 and the fan has stopped. What do I do?',
    a: 'On the Halvard AW-9, E21 means the fan has stalled, usually from ice. Switch to defrost and wait 10 minutes. If it returns, Brightwell Heating can fit a new fan motor under warranty.',
    source: 'Installation manual p.42 · Warranty active until Mar 2028',
  },
};

// The whole life of a product, unboxing to recycling. Each stage: the owner's moment + where AI helps.
export const lifecycle = {
  eyebrow: 'From unboxing to recycling',
  title: ['There for its', 'whole life.'],
  lede: 'One code on the product opens the right experience for each moment, for each person who scans it.',
  stages: [
    { k: 'Unbox', t: 'Set up right first time', d: 'Scan the box for the setup guide for that exact model, in the owner\'s language.', ai: 'Ask anything about setup.', status: 'live' as Status },
    { k: 'Register', t: 'Two taps, no password', d: 'Ownership confirmed, warranty started, consent captured.', ai: 'Fills in what it already knows.', status: 'live' as Status },
    { k: 'Use', t: 'Get more from it', d: 'Tips, settings and reminders that fit how it is used.', ai: 'Answers from your manuals, with sources.', status: 'live' as Status },
    { k: 'Fix', t: 'Solve it without a phone queue', d: 'Step-by-step fixes, the right part for that serial, or a person.', ai: 'Diagnoses the fault code for that unit.', status: 'live' as Status },
    { k: 'Service', t: 'Keep it working', d: 'A reminder before the service is due, booked with the installer, with the right parts on the van. Bulletins reach the units they are for.', ai: 'Tells the owner what is due, and why.', status: 'pilot' as Status },
    { k: 'Pass on', t: 'Sold, the record goes with it', d: 'The next owner takes over the warranty and history, with consent.', ai: 'Welcomes the new owner to their unit.', status: 'live' as Status },
    { k: 'Recycle', t: 'Its next chapter', d: 'How to return, refurbish or recycle it, with the materials data recyclers need.', ai: 'Points to take-back and the right route.', status: 'pilot' as Status },
  ],
};

// What it pays back (24 Oct 2026): the homepage's second beat is money, for an aftersales or service buyer.
// Replaces the old "Leave an impression" beliefs (left over from connected packaging). No invented figures.
export const belief = {
  eyebrow: 'What it pays back',
  title: ['Make aftersales'],
  swipe: 'pay',
  items: [
    { t: 'Registrations', d: 'Owners register at handover, from the plate, in under a minute.' },
    { t: 'Parts sold', d: 'Owners and dealers buy the part that fits that serial, from you.' },
    { t: 'Claims handled', d: 'Each claim arrives with serial, build, owner and history. Fewer calls, fewer no-fault returns.' },
  ],
};

// "Why this needs to exist": the manifesto page (/why). Founder's own framing, Oct 2026.
export const manifesto = {
  eyebrow: 'Why we exist',
  title: ['We have all', 'been there.'],
  open: 'You buy something good, and then you are on your own.',
  moments: [
    { k: 'The instructions', t: 'The instructions do not get it working.', d: 'A folded sheet in six languages and none of them yours. A diagram that skips the step you are stuck on.', img: '/images/v2/gen/why-instructions.jpg' },
    { k: 'The part', t: 'Something is missing.', d: 'One bolt short, or the wrong cable. No idea who to ask or what it is called.', img: '/images/v2/gen/why-missing-part.jpg' },
    { k: 'The card', t: 'Nobody registers it. Nobody reads the manual.', d: 'The warranty card goes in the drawer with the others. The maker never learns who you are.', img: '/images/v2/gen/why-drawer.jpg' },
    { k: 'The day it stops', t: 'It works. And then it does not.', d: 'An error code at the worst moment. A phone queue, a forum thread, a one-star review.', img: '/images/v2/gen/why-broken.jpg' },
  ],
  turn: {
    title: ['Not the owner\'s fault.', 'Bad for the maker, too.'],
    body: 'Every one of those moments is a cost someone else pays. Returns that were never faults. Support calls a manual should have answered. Parts sold by strangers. A second owner nobody knows exists. The relationship ends at the till, so the product is judged on its worst day.',
  },
  belief: {
    title: ['Things should not be', 'disposable.'],
    lines: [
      'A quality product should work well from the first day.',
      'It should tell you how to look after it.',
      'It should be easy to fix, by you or by someone who knows it.',
      'When it changes hands, its history should go with it.',
      'And when its life is over, it should know where it goes next.',
    ],
    img: '/images/v2/gen/why-repair.jpg',
  },
  disposable: {
    img: '/images/v2/gen/why-landfill.jpg',
    caption: 'Too much of what ends up in landfill was never worn out. It was abandoned.',
  },
  answer: {
    title: ['So every product', 'can keep in touch.'],
    body: 'Each product gets a living record and its own expert, one scan away, for every owner it has. The people who made it finally know who owns it.',
    points: ['Set up right first time', 'Serviced before it stops', 'The right part for that exact unit', 'Answers from the real manual, with sources', 'Repair before replace', 'A record that outlives the first owner', 'A next chapter: take-back, refurbishment and recycling'],
  },
  makers: {
    title: ['For the people who make things well.'],
    body: 'You already do the hard part. We help the product keep its promise after it leaves the factory.',
  },
  close: 'Make owning things better.',
};

// Signed forms: your existing PDFs become structured forms people sign on a phone (founder, 20 Oct).
export const forms = {
  eyebrow: 'Forms and signatures',
  title: ['Your paper forms,', 'signed on the spot.'],
  lede: 'Start from your own PDF, filed on the serial once signed.',
  steps: [
    { k: 'Your PDF', t: 'Start with the form you have', d: 'Upload the existing PDF. Nothing to redesign.' },
    { k: 'Structured', t: 'Fields, checks and signer slots', d: 'AI drafts the fields, checks and signers. Your team approves.' },
    { k: 'Signed', t: 'Signed on a phone, in person or by link', d: 'Installer, dealer and owner sign on one screen, with your consent wording.' },
    { k: 'Sealed', t: 'Sealed, verifiable and filed on the unit', d: 'A sealed PDF with an audit page, filed on that serial and case.' },
  ],
  status: 'pilot' as Status,
  examples: ['Commissioning certificate', 'Pre-delivery inspection', 'Thorough examination report', 'Handover and warranty start', 'Trade-in condition report'],
};

// Spares and accessories: revenue you are currently losing (founder, 20 Oct).
export const parts = {
  eyebrow: 'Spares and accessories',
  title: ['The right part,', 'from you or the dealer.'],
  lede: 'Owners see only the parts that fit their serial, sold by you or their dealer.',
  points: [
    { t: 'Fits that serial', d: 'Parts and accessories matched to the exact model, build and options of that unit.' },
    { t: 'Suggested at the right moment', d: 'When the AI explains a fault, it offers the part that fixes it. Service reminders offer the consumables.' },
    { t: 'Your channel, your choice', d: 'Sell direct, route to the owner\'s dealer or installer, or both.' },
    { t: 'Orders, payment and returns', d: 'Checkout under your brand, with orders and returns flowing back to your ERP.' },
    { t: 'After the warranty ends', d: 'The relationship keeps going: consumables, upgrades and the next product, sold to an owner you know.' },
  ],
  status: 'pilot' as Status,
  sample: { part: 'Fan motor assembly', fits: 'Fits KAW9-26-04182 · Halvard AW-9 (2026 build)', price: '£86.40', by: 'Fitted by Brightwell Heating, your installer' },
};

export const erp = {
  eyebrow: 'Works with your ERP',
  title: ['Your ERP stays', 'the master.'],
  lede: 'We read parts, serials and customers, and write back approved returns.',
  systems: [
    { name: 'Epicor Kinetic', status: 'live' as Status, href: '/integrations/epicor-kinetic', note: 'Native connector. Parts, packing slips, serials and customers flow in. If you switch returns on, approved returns become RMAs in Kinetic.' },
    { name: 'IFS Cloud', status: 'next' as Status, note: 'Our API and your integration partner today. Native connector on the roadmap.' },
    { name: 'SAP S/4HANA & Business One', status: 'next' as Status, note: 'Our API and your integration partner today. Native connector on the roadmap.' },
    { name: 'Anything else', status: 'live' as Status, note: 'Spreadsheet import, REST API and webhooks. Start with a CSV.' },
  ],
  flows: {
    // What the Epicor Kinetic connector reads and writes (see pages/integrations/connectors.ts). Packets in explainers/ErpSync.tsx follow this order.
    in: ['Parts & catalogue', 'Serials & lots', 'Customers', 'Packing slips', 'Customer PO'],
    out: ['Returns as RMAs'],
  },
  proof: 'Our first customer starts with scannable packing slips straight from its ERP.',
};

// Anonymised customer story. Do NOT add identifying details (name, town, founding year, product brands, people).
export const customerStory = {
  eyebrow: 'Customer story',
  who: 'A leading European manufacturer',
  descriptor: 'A precision engineering manufacturer',
  headline: 'Everything that leaves the factory, connected.',
  tagline: 'From packing slips to passports.',
  summary:
    'A manufacturer that would rather lead than follow. They start by putting a scannable code on every packing slip from their ERP, then add Digital Product Passports, with a code on each part, from the same ERP data.',
  chapters: [
    { k: 'The rare problem', t: 'Easy to deal with, on the rare day it matters', d: 'If something is not right, their customer scans the slip and says so in a minute. The case opens with the order attached, so the reply starts with the facts.' },
    { k: 'Ahead of the passport', t: 'Ready for the passport', d: 'The packing slip is the first step. Passports follow on a code for each part, from the same ERP data and the same platform, ready when the rules reach their products.' },
  ],
  facts: [
    { k: 'Status', v: 'Contracted' },
    { k: 'Starts with', v: 'Packing slips' },
    { k: 'Then', v: 'Digital Product Passport' },
    { k: 'ERP', v: 'Stays the master' },
  ],
};

// Trust as positioning: facts from the Trust Centre, Customer Terms and Price List.
export const trust = {
  eyebrow: 'Our promises',
  title: ['Your data, your customers,', 'your brand.'],
  lede: 'Owners see your brand. The Customer Terms make the data and the customers yours.',
  pledges: [
    { t: 'You own your data', d: 'Product records, registrations, conversations, AI outputs and your customers\' details all belong to you.', src: 'Customer Terms, clause 5.1', href: '/legal/terms/' },
    { t: 'AI built to serve you, not trained on your IP', d: 'Your product knowledge improves only your own assistant. No shared model and no AI provider trains on it, and you can switch AI off.', src: 'AI Statement', href: '/legal/ai/' },
    { t: 'Codes you control', d: 'Put codes on your own domain and you control them for as long as the product is in use.', src: 'Customer Terms, Schedule B', href: '/legal/terms/' },
    { t: 'Built for GDPR and EU rules', d: 'A data processing agreement in the terms and an appointed EU representative. The Data Act, AI Act and passport rules are designed in.', src: 'Data Processing Agreement', href: '/legal/dpa/' },
    { t: 'Products in the field come first', d: 'Public passports and product pages stay up, even in an account dispute.', src: 'Customer Terms, clause 12.3', href: '/legal/terms/' },
  ],
  honest: 'We publish which controls we have and which are planned. Cyber Essentials and an independent penetration test are on the plan.',
  facts: [
    { k: 'GDPR', v: 'DPA built into the terms' },
    { k: 'Uptime target', v: '99.5% monthly' },
    { k: 'Sign-in', v: 'Passkeys, no passwords for owners' },
    { k: 'EU representative', v: 'Appointed (GDPR Art. 27)' },
  ],
  docs: [
    { t: 'Trust Centre', href: '/trust/' },
    { t: 'Data Processing Agreement', href: '/legal/dpa/' },
    { t: 'Sub-processors', href: '/legal/subprocessors/' },
    { t: 'Service Level Agreement', href: '/legal/sla/' },
    { t: 'AI Statement', href: '/legal/ai/' },
    { t: 'Customer Terms', href: '/legal/terms/' },
    { t: 'Price List', href: '/legal/pricing/' },
    { t: 'Responsible disclosure', href: '/legal/responsible-disclosure/' },
  ],
};

export const dealers = {
  eyebrow: 'For dealers and installers',
  title: ['Your dealers stay in', 'every sale.'],
  lede: 'Direct relationships with owners make dealers nervous. So the dealer is part of the record.',
  points: [
    { t: 'Credited at registration', d: 'The selling dealer is recorded when the unit is registered, so the relationship is theirs as much as yours.' },
    { t: 'Handover in minutes', d: 'Pre-delivery checks and handover forms signed on a phone and sealed.' },
    { t: 'Work routed to them', d: 'Service requests and parts orders go to the right branch. No leads lost to the factory.' },
    { t: 'Full history at trade-in', d: 'When a machine comes back, the dealer sees what was done to it and when.' },
  ],
};

export const dpp = {
  eyebrow: 'Compliance and convenience',
  title: ['Passport data for each unit,', 'kept current from your ERP.'],
  lede: 'Passports and right to repair are coming. Meet them with the identity that makes the product easier to own.',
  contrast: {
    minimum: { label: 'The minimum', points: ['A static PDF behind a code', 'The same page for every unit', 'Updated when the regulator asks', 'A cost centre'] },
    better: { label: 'Compliance and convenience', points: ['The exact unit, its build and its history', 'The right view for owner, repairer, recycler or authority', 'Kept current from your ERP', 'An experience owners notice'] },
  },
  points: [
    'A persistent identifier per unit through a code on the product, packaging or documents, on your own domain',
    'Open, machine-readable data, kept current from your ERP: lots, materials and certificates attached at source',
    'Free access for owners, repairers, recyclers and authorities, each to the data their role allows',
    'Built to outlast you: passports must stay available even if the company behind them stops trading',
    'We say plainly what is decided and what is not. Last verified 20 October 2026',
  ],
  facts: [
    { t: 'Battery passports start on 18 February 2027', d: 'Including home storage batteries over 2 kWh, reached by a QR code.', src: 'Regulation (EU) 2023/1542, Art. 77' },
    { t: 'The EU passport registry went live on 20 July 2026', d: 'It records each passport\'s identifiers. Your data stays with you.', src: 'Regulation (EU) 2024/1781, Art. 13' },
    { t: 'Digital machinery instructions from 20 January 2027', d: 'Online for at least 10 years, with paper still available on request at purchase.', src: 'Regulation (EU) 2023/1230, Art. 10(7)' },
    { t: 'No other product group has a passport date yet', d: 'When one is adopted, its rules apply no earlier than 18 months later.', src: 'Regulation (EU) 2024/1781, Art. 4' },
  ],
  caveat: 'Certification for DPP service providers has not been defined yet. We design to the published requirements of Regulation (EU) 2024/1781 and will seek certification when a scheme exists.',
};

export type Industry = {
  slug: string;
  name: string;
  short: string;
  hero: string;
  sub: string;
  pains: string[];
  wins: string[];
  image: string;
  dppNote?: string;
};

// Ordered by ICP priority (leads analysis, Oct 2026)
export const industries: Industry[] = [
  {
    slug: 'agricultural-machinery',
    name: 'Agricultural machinery',
    short: 'Dealers, PDI and harvest-time uptime',
    hero: 'Know every machine in every field.',
    sub: 'PDI signed in the yard, and the right part before harvest stops.',
    pains: ['Warranty forms that arrive weeks after delivery', 'Second-hand machines nobody can trace', 'Parts lookups by phone in the busiest week of the year'],
    wins: ['Handover signed in the yard', 'Serial-exact parts and manuals', 'Bulletins that reach the operator'],
    image: '/images/v2/gen/agri-handover.jpg',
  },
  {
    slug: 'lifting-access-equipment',
    name: 'Lifting & inspected equipment',
    short: 'LOLER and PUWER records that travel with the unit',
    hero: 'The inspection record lives on the machine.',
    sub: 'Examinations and certificates on each serial, one scan for the next inspector.',
    pains: ['Inspection history scattered across hirers and service agents', 'Certificates that go missing when equipment changes hands', 'No way to reach the operators of an affected serial'],
    wins: ['Signed inspection forms per serial', 'History that survives resale and re-hire', 'Targeted safety notices'],
    image: '/images/v2/gen/lifting-inspection.jpg',
  },
  {
    slug: 'heating-heat-pumps',
    name: 'Heating & heat pumps',
    short: 'Installer registration and commissioning',
    hero: 'Warranty from the day it is commissioned.',
    sub: 'Installers commission on site, homeowners scan for help, you see every unit.',
    pains: ['Installer registration that depends on goodwill', 'Homeowners who never register', 'Commissioning records you cannot find at claim time'],
    wins: ['Commissioning signed on site', 'Annual service reminders under your brand', 'Claims with the install record attached'],
    image: '/images/v2/gen/heatpump-commission-b.jpg',
    dppNote: 'Heat pumps and space heaters are not yet scheduled for a product passport; their energy label data already sits in the EU database. The installation, warranty and service record is where owners and installers feel the difference today.',
  },
  {
    slug: 'commercial-kitchen',
    name: 'Commercial kitchen equipment',
    short: 'Compliance logs that survive a change of operator',
    hero: 'Service history that stays with the appliance.',
    sub: 'Gas safety, service and parts history, ready for the next engineer.',
    pains: ['Paper service logs that leave with the old operator', 'Engineers arriving without the model history', 'Parts ordered by description, not serial'],
    wins: ['Service history per appliance', 'Engineer access by scan', 'Ownership transfer when sites are sold'],
    image: '/images/v2/gen/kitchen-service-b.jpg',
  },
  {
    slug: 'industrial-precision',
    name: 'Industrial & precision manufacturing',
    short: 'Traceability from ERP to customer',
    hero: 'Every delivery leaves with its own record.',
    sub: 'Each packing slip becomes one scan: what shipped, which lot, which certificate.',
    pains: ['Delivery problems handled by phone and email', 'Certificates sent separately and lost', 'Passport rules on the way for iron, steel and aluminium'],
    wins: ['Scannable packing slips from your ERP', 'Cases with lot and certificate attached', 'Passport-ready lot data'],
    image: '/images/v2/gen/precision-packing.jpg',
    dppNote: 'Iron and steel are the first product group lined up for passport rules under the EU working plan, with no date adopted yet. Lot and certificate data captured today is the foundation.',
  },
  {
    slug: 'premium-durables',
    name: 'Premium durables',
    short: 'Hi-fi, outdoor, caravans',
    hero: 'A record that lasts as long as the product.',
    sub: 'Owners register by scan, and every later owner joins the record.',
    pains: ['Owners you only meet when something breaks', 'A thriving second-hand market you cannot see', 'Support calls for questions a manual could answer'],
    wins: ['Owners registered by scan', 'Resale that keeps the history', 'Support grounded in your own documents'],
    image: '/images/v2/gen/hifi-owner.jpg',
  },
  {
    slug: 'medical-laboratory-instruments',
    name: 'Medical & laboratory instruments',
    short: 'PPM, calibration and field safety notices per serial',
    hero: 'Every instrument keeps its own service history.',
    sub: 'Calibration signed at the bench, safety notices sent to the current lab.',
    pains: ['Calibration certificates scattered across engineers and inboxes', 'Field safety notices that stop at the distributor', 'Refurbished units resold with no service history'],
    wins: ['PPM and calibration signed per serial', 'Notices that reach affected serials and their current owners', 'History that survives a move or a resale'],
    image: '/images/v2/gen/instr-ppm.jpg',
  },
  {
    slug: 'sports-fitness-equipment',
    name: 'Sports & fitness equipment',
    short: 'Service contracts, inspections and home warranty',
    hero: 'Every machine on the gym floor has its own record.',
    sub: 'Service signed on site, error codes answered from your own manuals.',
    pains: ['Service records split between operators, dealers and engineers', 'Error codes answered by phone, one call at a time', 'Used commercial kit resold with no history'],
    wins: ['Service and inspection signed per serial', 'Error code help from your own manuals', 'Warranty registration by scan, at home or in the gym'],
    image: '/images/v2/gen/fitness-service.jpg',
  },
];

nav.primary[1].items = industries.map((i) => ({ label: i.name, href: `/industries/${i.slug}`, desc: i.short }));

// How a manufacturer starts (founder framing, 20 Oct 2026). Say it once, no extra words.
export const startPath = ['Start with one product line', 'Understand who your customers are', 'Build a feedback loop to improve', 'Increase revenue'];

export const pricing = {
  eyebrow: 'Pricing',
  title: ['Start with one product line.', 'Grow from what you learn.'],
  lede: 'Learn who owns your products, improve the next one, and grow from there. No per-user fees.',
  plans: [
    {
      name: 'Connect',
      price: '£699',
      eur: '€849',
      period: 'per month, month to month',
      for: 'One product line, as a pilot',
      includes: ['5,000 tags a year included', '250 AI resolutions a month', 'Billed monthly, month to month'],
      cta: 'Start a pilot',
    },
    {
      name: 'Scale',
      price: '£2,500',
      eur: '€3,000',
      period: 'per month, annual paid monthly',
      for: 'Your whole range, annual, dealer add-on available',
      includes: ['25,000 tags a year included', '1,000 AI resolutions a month', 'Annual, paid monthly', 'Dealer add-on available'],
      cta: 'Book 30 minutes',
      featured: true,
    },
    {
      name: 'Enterprise',
      price: 'By quote',
      eur: '',
      period: 'per Order Form',
      for: 'Multiple brands, regions or plants',
      includes: ['Priced by quote', 'Set out in your Order Form'],
      cta: 'Talk to us',
    },
  ],
  dealerAddon: 'Dealers and installers: £9,000 a year for up to 100 branches, unlimited dealer users. Requires Scale.',
};

// Team credibility WITHOUT names or "founders" framing (user: naming founders makes us look small).
export const founders = {
  eyebrow: 'Who builds it',
  title: ['We have built ERP', 'for manufacturers before.'],
  lede: 'Our team has spent careers building ERP and AI for manufacturers.',
  people: [] as { name: string; role: string; bio: string }[],
  credentials: [
    { k: 'ERP', v: 'Built and sold an ERP business serving 160+ manufacturers' },
    { k: 'Scale', v: 'Led product and engineering at global software companies' },
    { k: 'AI', v: 'Shipped generative and agentic AI in production' },
  ],
  badges: ['Built ERP', 'Built AI agents', 'Scaled B2B SaaS'],
};

export const faqs = [
  { q: 'Do owners need an app?', a: 'No. They scan the code with their phone camera and get a page built for that exact unit. Sign-in uses a one-time code or a passkey, never a password.' },
  { q: 'Will this upset our dealers?', a: 'The selling dealer is credited at registration, service and parts requests go to them, and they see the full history at trade-in.' },
  { q: 'Do we have to change how we work in our ERP?', a: 'No. Your ERP stays the master. We read parts, serials and customers from it, and approved returns can go back as RMAs. Epicor Kinetic has a native connector; other ERPs connect through our API.' },
  { q: 'Who owns the data and what the AI learns?', a: 'You do. Your records, registrations, conversations and AI outputs belong to you. Your product knowledge improves only your own assistant, never a shared model, and you can switch AI off.' },
  { q: 'What happens to our QR codes if we leave?', a: 'They keep working. We redirect your links to a new host free for 12 months, or keep them live with Link Continuity. If the codes use your own domain, you can repoint them yourself at any time.' },
  { q: 'How do you handle GDPR?', a: 'A data processing agreement is built into our terms, we have an appointed EU representative, and every sub-processor is published in the legal hub.' },
  { q: 'How long does it take to go live?', a: 'A pilot on one product line takes about four weeks, from kick-off to scannable codes from your ERP.' },
  { q: 'Are we in scope for the Digital Product Passport?', a: 'It depends on your product group and its delegated act, and dates differ by category. On the call we will tell you what is decided for your products.' },
];

export const booking = {
  title: ['Book 30 minutes.', 'Bring one product.'],
  lede: 'Name one product you make, and see its record live.',
  expect: [
    'We look at one of your products together',
    'You see the owner, dealer and brand views live',
    'You leave with clear answers on fit, effort and price',
  ],
  booked: {
    title: ["You're booked.", 'Bring one product.'],
    lede: "In 30 minutes we'll show you what its owners, dealers and service team could see.",
    see: [
      { t: 'One of your products, connected', d: 'A record for one serial, opened from its code.' },
      { t: 'What its owner sees', d: 'Registration, manuals, warranty, parts and service, in your brand.' },
      { t: 'What it takes', d: 'How it connects to your ERP, how long it takes and what it costs.' },
    ],
  },
};

export const footer = {
  columns: [
    { t: 'Platform', links: [{ l: 'How it works', h: '/platform' }, { l: 'Dealers', h: '/platform/dealers' }, { l: 'Ownership transfer', h: '/platform/ownership' }, { l: 'Digital Product Passport', h: '/platform/digital-product-passport' }, { l: 'ERP integrations', h: '/integrations' }, { l: 'Interactive tour', h: '/tour' }] },
    { t: 'Industries', links: industries.map((i) => ({ l: i.name, h: `/industries/${i.slug}` })) },
    { t: 'Company', links: [{ l: 'Why we exist', h: '/why' }, { l: 'Customer story', h: '/customers' }, { l: 'Why trust us', h: '/why-trust-us' }, { l: 'Pricing', h: '/pricing' }, { l: 'Blog', h: '/blog/' }, { l: 'Book a call', h: '/book-a-demo' }] },
    { t: 'Trust & legal', links: [{ l: 'Trust Centre', h: '/trust/' }, { l: 'Legal hub', h: '/legal/' }, { l: 'Privacy', h: '/legal/privacy/' }, { l: 'Cookies', h: '/legal/cookies/' }, { l: 'Accessibility', h: '/legal/accessibility/' }, { l: 'Security.txt', h: '/.well-known/security.txt' }] },
  ],
  legal: 'Branded Mark Limited, registered in England and Wales, company number 14452041. Broomfield House, Blakedown, Kidderminster, DY10 3LA.',
};
